import { useResetOnClose } from "@/lib/useResetOnClose";
import { BranchTag } from "@/components/BranchTag";
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X, Printer, Eye, MoreVertical, Search, SlidersHorizontal, RotateCcw, CalendarDays, Building2, User, ChevronLeft, ChevronRight, ChevronsUpDown, List, LayoutGrid, PackageCheck, Ban, FolderInput } from "lucide-react";
import { api } from "@/lib/api";
import { openPdf } from "@/lib/print";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { usePermissions } from "@/lib/permissions";
import { useCompany } from "@/context/CompanyContext";
import { PageBreadcrumb } from "@/components/PageBreadcrumb";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

import { fmtMoney, fmtDate, partyLabel } from "@/lib/format";
import { CompanyField } from "@/components/CompanyField";
import { confirmAction } from "@/lib/dialogs";
import { BudgetOverrideDialog } from "@/components/gates/BudgetOverrideDialog";
import { budgetExceeded } from "@/lib/budgetOverride";
// po-design: filter bar, List / Card toggle, sortable table with delivery progress bars, pagination (po-receipts)
const EMPTY_ITEM = { description: "", quantity: "", rate: "" };
const STATUS_OPTIONS = [["DRAFT", "Draft"], ["SUBMITTED", "Pending approval"], ["APPROVED", "Approved"], ["SUB_APPROVED", "Sub-approved"], ["REJECTED", "Rejected"], ["CLOSED", "Closed"], ["CANCELLED", "Cancelled"]];
const DELIVERY_OPTIONS = [["AWAITING", "Awaiting delivery"], ["NOT_RECEIVED", "Not received"], ["PARTIAL", "Partially received"], ["FULL", "Fully received"]];
const DELIVERABLE = ["FULL", "PARTIAL", "NOT_RECEIVED"];
const PAGE_SIZES = [10, 25, 50];
const NO_FILTERS = { search: "", status: "", project: "", from: "", to: "", delivery: "" };
const TH = "h-11 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground";
const timeOf = (iso) => {
  const d = iso ? new Date(iso) : null;
  return d && !Number.isNaN(d.getTime()) ? d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", hour12: true }).toLowerCase() : "";
};
const inr = (v) => `₹${fmtMoney(v)}`;

function DeliveryCell({ r }) {
  if (!r || !DELIVERABLE.includes(r.status)) return <span className="text-xs text-muted-foreground">—</span>;
  const n = (v) => Number(v).toLocaleString("en-IN", { maximumFractionDigits: 3 });
  const pct = r.ordered > 0 ? Math.min((r.received / r.ordered) * 100, 100) : 0;
  const label = { FULL: "Fully received", PARTIAL: "Partially received", NOT_RECEIVED: "Not received" }[r.status];
  const bar = { FULL: "bg-emerald-500", PARTIAL: "bg-sky-500", NOT_RECEIVED: "bg-slate-300" }[r.status];
  return (
    <div className="w-36 space-y-1" data-testid={`po-delivery-${r.number}`}>
      <p className="text-xs text-foreground">{label}</p>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-secondary"><div className={`h-full rounded-full ${bar}`} style={{ width: `${pct}%` }} /></div>
      <p className="text-[11px] tabular-nums text-muted-foreground">{n(r.received)} / {n(r.ordered)}{r.draft_grns > 0 ? ` · ${r.draft_grns} draft GRN` : ""}</p>
    </div>
  );
}

function SortHead({ label, k, sort, onSort, className = "" }) {
  return (
    <TableHead className={`${TH} ${className}`}>
      <button type="button" onClick={() => onSort(k)} className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-foreground" data-testid={`po-sort-${k}`}>
        {label}<ChevronsUpDown className={`h-3 w-3 ${sort.key === k ? "text-[#910029]" : "opacity-60"}`} />
      </button>
    </TableHead>
  );
}

export default function PurchaseOrders() {
  const pf = useProjectFilter();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can("PURCHASE_ORDERS", "create");
  const canLink = can("PURCHASE_ORDERS", "edit"), canRemove = can("PURCHASE_ORDERS", "delete");
  const { companies } = useCompany() || {};
  const [pos, setPos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [rfqs, setRfqs] = useState([]);
  const [requisitions, setRequisitions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [rec, setRec] = useState({});  // po-receipts
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [overBudget, setOverBudget] = useState(null);       // a submit the server stopped because it is over budget: { row, detail }
  const [f, setF] = useState(NO_FILTERS);
  const [sort, setSort] = useState({ key: "date", dir: "desc" });
  const [mode, setMode] = useState("list");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [selected, setSelected] = useState([]);
  const [viewing, setViewing] = useState(null);
  const [linking, setLinking] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [poRes, vendorRes, rfqRes, prRes, projRes] = await Promise.all([
        api.get("/procurement/purchase-orders"), api.get("/procurement/vendors"),
        api.get("/procurement/rfqs").catch(() => ({ data: [] })), api.get("/procurement/requisitions").catch(() => ({ data: [] })),
        api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setPos(poRes.data);
      setVendors(vendorRes.data);
      setRfqs(rfqRes.data);
      setRequisitions(prRes.data);
      setProjects(projRes.data);
      api.get("/procurement/po-receipts").then(({ data }) => setRec(Object.fromEntries((data || []).map((r) => [r.po_id, r])))).catch(() => {});
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [f, sort, pageSize, pf.value]);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || "—";
  const projectOf = (po) => projects.find((p) => p.id === po.project_id);
  const projectLabel = (po) => { const p = projectOf(po); return p ? `${p.code} — ${p.name}` : ""; };
  const companyName = (id) => (companies || []).find((c) => c.id === id)?.name || "";
  const set = (patch) => setF({ ...f, ...patch });

  const onSort = (key) => setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "date" || key === "total" ? "desc" : "asc" }));
  const sortVal = (po, key) => {
    if (key === "date") return po.created_at || po.po_date;
    if (key === "total") return Number(po.total_amount || 0);
    if (key === "status") return po.status;
    if (key === "delivery") { const r = rec[po.id]; return r && DELIVERABLE.includes(r.status) ? (r.ordered ? r.received / r.ordered : 0) : -1; }
    return po.number;
  };

  const shown = useMemo(() => {
    const q = f.search.trim().toLowerCase();
    let list = pf.apply(pos, (p) => p.project_id).filter((po) => {
      const r = rec[po.id];
      if (f.status && po.status !== f.status) return false;
      if (f.project && po.project_id !== f.project) return false;
      if (f.from && po.po_date < f.from) return false;
      if (f.to && po.po_date > f.to) return false;
      if (f.delivery === "AWAITING" ? !["NOT_RECEIVED", "PARTIAL"].includes(r?.status) : f.delivery && r?.status !== f.delivery) return false;
      if (!q) return true;
      return [po.number, vendorName(po.vendor_id), projectLabel(po), ...(po.items || []).map((i) => i.description)].some((v) => String(v || "").toLowerCase().includes(q));
    });
    list = [...list].sort((a, b) => {
      const x = sortVal(a, sort.key), y = sortVal(b, sort.key);
      const c = typeof x === "number" ? x - y : String(x).localeCompare(String(y));
      return sort.dir === "asc" ? c : -c;
    });
    return list;
  }, [pos, rec, f, sort, pf.value, vendors, projects]);

  const pages = Math.max(1, Math.ceil(shown.length / pageSize));
  const cur = Math.min(page, pages);
  const rows = shown.slice((cur - 1) * pageSize, cur * pageSize);
  const from = shown.length ? (cur - 1) * pageSize + 1 : 0;
  const allOnPage = rows.length > 0 && rows.every((p) => selected.includes(p.id));
  const toggleAll = () => setSelected(allOnPage ? selected.filter((id) => !rows.some((p) => p.id === id)) : [...new Set([...selected, ...rows.map((p) => p.id)])]);
  const advancedOn = Boolean(f.delivery);
  const dirty = JSON.stringify(f) !== JSON.stringify(NO_FILTERS) || sort.key !== "date" || sort.dir !== "desc";

  const act = async (po, action, extra) => {
    setBusyId(po.id);
    try {
      await api.post(`/procurement/purchase-orders/${po.id}/${action}`, action === "submit" ? extra : {});
      toast.success(`PO ${action}ed`);
      load();
    } catch (err) {
      const over = action === "submit" ? budgetExceeded(err) : null;
      if (over) setOverBudget({ row: po, detail: over });
      else toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };
  const cancelPo = async (po) => {
    if (!await confirmAction(`Cancel PO ${po.number}? Its RFQ becomes editable again. This is blocked if goods or bills already exist against it.`)) return;
    setBusyId(po.id);
    try {
      await api.post(`/procurement/purchase-orders/${po.id}/cancel`, {});
      toast.success(`PO ${po.number} cancelled`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };
  const remove = async (po) => {
    if (!await confirmAction(`Delete PO ${po.number}? This can't be undone.`)) return;
    try {
      await api.delete(`/procurement/purchase-orders/${po.id}`);
      toast.success(`PO ${po.number} deleted`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };
  const linkProject = async (po, projectId) => {
    try {
      await api.patch(`/procurement/purchase-orders/${po.id}/project`, { project_id: projectId });
      toast.success(projectId ? "PO linked to project — its material cost now counts there" : "PO unlinked from project");
      setLinking(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const projectCell = (po) => {
    const p = projectOf(po);
    if (!p) {
      return canLink && po.status !== "CANCELLED"
        ? <button type="button" onClick={() => setLinking(po)} className="rounded-md border border-amber-500/60 px-2 py-1 text-xs text-amber-600 dark:text-amber-400" data-testid={`po-project-select-${po.number}`}>Not linked — link</button>
        : <span className="text-xs text-muted-foreground">—</span>;
    }
    return (
      <div className="flex items-center gap-2.5">
        <span className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-secondary text-muted-foreground"><Building2 className="h-4 w-4" /></span>
        <div className="min-w-0"><p className="text-sm font-medium leading-tight text-foreground">{p.code} — {p.name}</p><p className="text-[11px] text-muted-foreground">{companyName(po.company_id)}</p></div>
      </div>
    );
  };
  const firstLine = (po) => {
    const it = (po.items || [])[0];
    if (!it) return null;
    return (
      <p className="mt-0.5 whitespace-nowrap text-[11px] text-muted-foreground">
        {it.description} — {Number(it.quantity)} @ ₹{fmtMoney(it.rate)}{it.is_capital && <span className="ml-1.5 rounded bg-violet-500/10 px-1.5 py-0.5 text-[10px] text-violet-600 dark:text-violet-400">Asset</span>}
        {po.items.length > 1 && <span className="ml-1.5">+ {po.items.length - 1} more</span>}
      </p>
    );
  };
  const rowMenu = (po) => {
    const r = rec[po.id];
    const awaiting = r && ["NOT_RECEIVED", "PARTIAL"].includes(r.status);
    const canDelete = canRemove && ["DRAFT", "REJECTED", "CANCELLED"].includes(po.status);
    const items = [
      canWrite && po.status === "DRAFT" && <DropdownMenuItem key="s" disabled={busyId === po.id} onSelect={() => act(po, "submit")} data-testid={`po-submit-${po.number}`}><Send className="h-4 w-4" /> Submit for approval</DropdownMenuItem>,
      po.status === "SUBMITTED" && <DropdownMenuItem key="a" onSelect={() => act(po, "approve")} data-testid={`po-approve-${po.number}`}><Check className="h-4 w-4" /> Approve</DropdownMenuItem>,
      po.status === "SUBMITTED" && <DropdownMenuItem key="r" className="text-rose-600 focus:text-rose-600" onSelect={() => act(po, "reject")} data-testid={`po-reject-${po.number}`}><X className="h-4 w-4" /> Reject</DropdownMenuItem>,
      awaiting && <DropdownMenuItem key="g" onSelect={() => navigate("/erp/operations/goods-receipt-notes")} data-testid={`po-receive-${po.number}`}><PackageCheck className="h-4 w-4" /> Record goods receipt</DropdownMenuItem>,
      canLink && po.status !== "CANCELLED" && <DropdownMenuItem key="p" onSelect={() => setLinking(po)} data-testid={`po-link-${po.number}`}><FolderInput className="h-4 w-4" /> Change project</DropdownMenuItem>,
      canWrite && po.status === "APPROVED" && <DropdownMenuItem key="c" className="text-rose-600 focus:text-rose-600" disabled={busyId === po.id} onSelect={() => cancelPo(po)} data-testid={`po-cancel-${po.number}`}><Ban className="h-4 w-4" /> Cancel PO</DropdownMenuItem>,
      canDelete && <DropdownMenuItem key="d" className="text-rose-600 focus:text-rose-600" onSelect={() => remove(po)} data-testid={`po-delete-${po.number}`}><Trash2 className="h-4 w-4" /> Delete PO</DropdownMenuItem>,
    ].filter(Boolean);
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild><button type="button" title="More" data-testid={`po-actions-${po.number}`} className="grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"><MoreVertical className="h-4 w-4" /></button></DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="min-w-[200px]">{items.length ? items : <DropdownMenuItem disabled>No other actions</DropdownMenuItem>}</DropdownMenuContent>
      </DropdownMenu>
    );
  };
  const iconBtn = "grid h-8 w-8 place-items-center rounded-md text-muted-foreground hover:bg-[#F5E6EC] hover:text-[#910029]";
  const pagerBtn = "grid h-9 w-9 place-items-center rounded-lg border border-border text-muted-foreground hover:bg-accent disabled:opacity-40";
  const rangeLabel = f.from || f.to ? `${f.from ? fmtDate(f.from) : "From"} – ${f.to ? fmtDate(f.to) : "To"}` : "From - To";

  return (
    <div className="space-y-5" data-testid="purchase-orders-page">
      {overBudget && <BudgetOverrideDialog detail={overBudget.detail} what="purchase order" onCancel={() => setOverBudget(null)}
        onSubmit={(reason) => { const row = overBudget.row; setOverBudget(null); act(row, "submit", { budget_override_reason: reason }); }} />}
      <div className="space-y-2">
        <PageBreadcrumb crumbs={[{ label: "Procurement" }, { label: "Purchase Orders" }]} />
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-bold tracking-tight text-foreground">Purchase Orders</h1>
            <p className="mt-1 max-w-3xl text-sm text-muted-foreground">Manage and track all purchase orders across your projects and vendors.</p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ProjectFilter filter={pf} />
            {canWrite && <CreatePODialog companies={companies} vendors={((l) => (l.length ? l : vendors))(vendors.filter((v) => (v.vendor_types || []).includes("PURCHASE")))} rfqs={rfqs} requisitions={requisitions} projects={projects} onCreated={load} />}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2.5 rounded-2xl border border-border bg-card p-3.5 shadow-sm" data-testid="po-filter-bar">
        <div className="relative min-w-[220px] flex-[2]">
          <Search className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="po-search-input" value={f.search} onChange={(e) => set({ search: e.target.value })} placeholder="Search by PO number, vendor, project..."
            className="h-11 w-full rounded-lg border border-border bg-background pl-10 pr-3 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-[#910029] focus:ring-2 focus:ring-[#910029]/20" />
        </div>
        <Select value={f.status || "ALL"} onValueChange={(v) => set({ status: v === "ALL" ? "" : v })}>
          <SelectTrigger className="h-11 w-44 text-sm" data-testid="po-filter-status"><SelectValue placeholder="All Statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All Statuses</SelectItem>{STATUS_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={f.project || "ALL"} onValueChange={(v) => set({ project: v === "ALL" ? "" : v })}>
          <SelectTrigger className="h-11 w-44 text-sm [&>span]:truncate" data-testid="po-filter-project"><SelectValue placeholder="All Projects" /></SelectTrigger>
          <SelectContent><SelectItem value="ALL">All Projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" data-testid="po-filter-dates" className="inline-flex h-11 min-w-[11rem] items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm text-muted-foreground hover:bg-accent">
              <CalendarDays className="h-4 w-4" /><span className={f.from || f.to ? "text-foreground" : ""}>{rangeLabel}</span>
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 space-y-3">
            <div><Label className="text-xs">From</Label><Input type="date" value={f.from} onChange={(e) => set({ from: e.target.value })} className="mt-1 h-9" data-testid="po-filter-from" /></div>
            <div><Label className="text-xs">To</Label><Input type="date" value={f.to} onChange={(e) => set({ to: e.target.value })} className="mt-1 h-9" data-testid="po-filter-to" /></div>
          </PopoverContent>
        </Popover>
        <Popover>
          <PopoverTrigger asChild>
            <button type="button" title="More filters" data-testid="po-filter-more" className="relative grid h-11 w-11 place-items-center rounded-lg border border-border bg-background text-foreground hover:bg-accent">
              <SlidersHorizontal className="h-4 w-4" />{advancedOn && <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-[#910029]" />}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 space-y-3">
            <p className="text-sm font-semibold text-foreground">More filters</p>
            <div>
              <Label className="text-xs">Delivery</Label>
              <Select value={f.delivery || "ALL"} onValueChange={(v) => set({ delivery: v === "ALL" ? "" : v })}>
                <SelectTrigger className="mt-1 h-9 text-sm" data-testid="po-filter-delivery"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="ALL">All</SelectItem>{DELIVERY_OPTIONS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </PopoverContent>
        </Popover>
        <button type="button" onClick={() => { setF(NO_FILTERS); setSort({ key: "date", dir: "desc" }); }} disabled={!dirty} data-testid="po-filter-reset" className="inline-flex h-11 items-center gap-1.5 px-3 text-sm text-muted-foreground hover:text-foreground disabled:opacity-50">
          <RotateCcw className="h-4 w-4" /> Reset
        </button>
      </div>

      <div className="rounded-2xl border border-border bg-card p-4 shadow-sm sm:p-5">
        <div className="mb-3 flex justify-end">
          <div className="inline-flex overflow-hidden rounded-lg border border-border" role="group" data-testid="po-view-toggle">
            {[["list", "List", List], ["card", "Card", LayoutGrid]].map(([k, label, Icon]) => (
              <button key={k} type="button" onClick={() => setMode(k)} aria-pressed={mode === k} data-testid={`po-view-${k}`}
                className={`inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium ${mode === k ? "bg-[#910029] text-white" : "bg-card text-muted-foreground hover:bg-accent"}`}><Icon className="h-4 w-4" />{label}</button>
            ))}
          </div>
        </div>

        {!loading && shown.length === 0 ? (
          <div className="rounded-xl border border-border"><EmptyState title={pos.length === 0 ? "No purchase orders yet" : "No purchase orders match"} description={pos.length === 0 ? undefined : "Try changing or resetting the filters."} testId="purchase-orders-empty-state" /></div>
        ) : mode === "list" ? (
          <div className="overflow-x-auto rounded-xl border border-border">
            <Table>
              <TableHeader><TableRow className="bg-secondary/60 hover:bg-secondary/60">
                <TableHead className="h-11 w-12 pl-5"><Checkbox checked={allOnPage} onCheckedChange={toggleAll} aria-label="Select all" data-testid="po-select-all" /></TableHead>
                <TableHead className={TH}>PO Number</TableHead><TableHead className={TH}>Project</TableHead><TableHead className={TH}>Vendor</TableHead>
                <SortHead label="Date" k="date" sort={sort} onSort={onSort} /><SortHead label="Total Value" k="total" sort={sort} onSort={onSort} className="text-right" />
                <SortHead label="Status" k="status" sort={sort} onSort={onSort} /><SortHead label="Delivery" k="delivery" sort={sort} onSort={onSort} />
                <TableHead className={`${TH} pr-5 text-right`}>Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((po) => (
                  <TableRow key={po.id} className="h-[76px]" data-testid={`po-row-${po.number}`} data-state={selected.includes(po.id) ? "selected" : undefined}>
                    <TableCell className="pl-5"><Checkbox checked={selected.includes(po.id)} onCheckedChange={(v) => setSelected(v ? [...selected, po.id] : selected.filter((id) => id !== po.id))} aria-label={`Select ${po.number}`} /></TableCell>
                    <TableCell>
                      <p className="text-sm font-semibold text-foreground">{po.number}<BranchTag row={po} /></p>
                      {po.rfq_id && <p className="text-[11px] text-sky-600 dark:text-sky-400">From RFQ {rfqs.find((r) => r.id === po.rfq_id)?.number || "—"}</p>}
                      {firstLine(po)}
                    </TableCell>
                    <TableCell>{projectCell(po)}</TableCell>
                    <TableCell><div className="flex items-center gap-2"><User className="h-4 w-4 shrink-0 text-muted-foreground" /><span className="text-sm text-foreground">{vendorName(po.vendor_id)}</span></div></TableCell>
                    <TableCell className="whitespace-nowrap text-sm text-foreground"><div>{fmtDate(po.po_date)}</div><div className="text-xs text-muted-foreground">{timeOf(po.created_at)}</div></TableCell>
                    <TableCell className="text-right text-sm font-semibold tabular-nums text-foreground">{inr(po.total_amount)}</TableCell>
                    <TableCell><StatusPill value={po.status} /></TableCell>
                    <TableCell><DeliveryCell r={rec[po.id]} /></TableCell>
                    <TableCell className="pr-5">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" title="View" onClick={() => setViewing(po)} data-testid={`po-actions-${po.number}-view`} className={iconBtn}><Eye className="h-4 w-4" /></button>
                        <button type="button" title="Print / PDF" onClick={() => openPdf(`/print/purchase-orders/${po.id}`)} data-testid={`po-print-${po.number}`} className={iconBtn}><Printer className="h-4 w-4" /></button>
                        {rowMenu(po)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="po-cards">
            {rows.map((po) => (
              <div key={po.id} className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4" data-testid={`po-card-${po.number}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0"><p className="text-sm font-semibold text-foreground">{po.number}</p>{firstLine(po)}</div>
                  <StatusPill value={po.status} />
                </div>
                <div className="space-y-2">{projectCell(po)}<div className="flex items-center gap-2 text-sm"><User className="h-4 w-4 text-muted-foreground" />{vendorName(po.vendor_id)}</div></div>
                <div className="flex items-end justify-between gap-3">
                  <div><p className="text-xs text-muted-foreground">{fmtDate(po.po_date)}</p><p className="text-base font-semibold tabular-nums text-foreground">{inr(po.total_amount)}</p></div>
                  <DeliveryCell r={rec[po.id]} />
                </div>
                <div className="flex items-center justify-end gap-1 border-t border-border pt-2">
                  <button type="button" title="View" onClick={() => setViewing(po)} className={iconBtn}><Eye className="h-4 w-4" /></button>
                  <button type="button" title="Print / PDF" onClick={() => openPdf(`/print/purchase-orders/${po.id}`)} className={iconBtn}><Printer className="h-4 w-4" /></button>
                  {rowMenu(po)}
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center justify-between gap-3" data-testid="po-pager">
          <p className="text-sm text-muted-foreground">Showing {from} - {from ? from + rows.length - 1 : 0} of {shown.length}{selected.length > 0 ? ` · ${selected.length} selected` : ""}</p>
          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Items per page</span>
            <Select value={String(pageSize)} onValueChange={(v) => setPageSize(Number(v))}>
              <SelectTrigger className="h-9 w-20 text-sm" data-testid="po-page-size"><SelectValue /></SelectTrigger>
              <SelectContent>{PAGE_SIZES.map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
            </Select>
            <button type="button" disabled={cur <= 1} onClick={() => setPage(cur - 1)} aria-label="Previous page" className={pagerBtn}><ChevronLeft className="h-4 w-4" /></button>
            {Array.from({ length: pages }, (_, i) => i + 1).slice(Math.max(0, cur - 3), Math.max(0, cur - 3) + 5).map((n) => (
              <button key={n} type="button" onClick={() => setPage(n)} aria-current={n === cur ? "page" : undefined}
                className={`grid h-9 w-9 place-items-center rounded-lg border text-sm font-semibold ${n === cur ? "border-[#910029] bg-[#910029] text-white" : "border-border text-muted-foreground hover:bg-accent"}`}>{n}</button>
            ))}
            <button type="button" disabled={cur >= pages} onClick={() => setPage(cur + 1)} aria-label="Next page" className={pagerBtn}><ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>

      <ViewPODialog po={viewing} rec={viewing ? rec[viewing.id] : null} onClose={() => setViewing(null)} vendorName={vendorName} projects={projects} />
      <LinkProjectDialog po={linking} projects={projects} onClose={() => setLinking(null)} onSave={linkProject} />
    </div>
  );
}

function LinkProjectDialog({ po, projects, onClose, onSave }) {
  const [value, setValue] = useState("none");
  useResetOnClose(Boolean(!!po), () => { setValue("none"); });      // closed without saving: opens fresh next time
  useEffect(() => { setValue(po?.project_id || "none"); }, [po]);
  return (
    <Dialog open={!!po} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="po-link-dialog">
        <DialogHeader><DialogTitle>Project for {po?.number}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">The site this material is for — its cost is booked to that project.</p>
        <Select value={value} onValueChange={setValue}>
          <SelectTrigger data-testid="po-link-select"><SelectValue placeholder="Not linked" /></SelectTrigger>
          <SelectContent><SelectItem value="none">Not linked</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onSave(po, value === "none" ? null : value)} data-testid="po-link-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ViewPODialog({ po, rec, onClose, vendorName, projects }) {
  const project = po ? projects.find((p) => p.id === po.project_id) : null;
  return (
    <Dialog open={!!po} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-3xl" data-testid="po-view-dialog">
        {po && (<>
          <DialogHeader><DialogTitle>Purchase Order {po.number}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm">
            <div><span className="text-muted-foreground">Vendor: </span>{vendorName(po.vendor_id)}</div>
            <div><span className="text-muted-foreground">Date: </span>{po.po_date}</div>
            <div><span className="text-muted-foreground">Project: </span>{project ? `${project.code} — ${project.name}` : "Not linked"}</div>
            <div className="flex items-center gap-2"><span className="text-muted-foreground">Status: </span><StatusPill value={po.status} /></div>
          </div>
          <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
            <Table>
              <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Description</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Received</TableHead><TableHead className="text-right">Pending</TableHead><TableHead className="text-right">Rate</TableHead><TableHead className="text-right">Amount</TableHead></TableRow></TableHeader>
              <TableBody>
                {(po.items || []).map((it, i) => (
                  <TableRow key={it.id}>
                    <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                    <TableCell>{it.description}{it.is_capital && <span className="ml-1 rounded bg-violet-500/10 px-1 text-[10px] text-violet-600 dark:text-violet-400">Asset</span>}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{Number(it.quantity)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{rec?.items?.[it.id] ? rec.items[it.id].received : "—"}</TableCell>
                    <TableCell className={`text-right font-mono text-xs ${rec?.items?.[it.id]?.pending > 0 ? "font-semibold text-amber-600 dark:text-amber-400" : ""}`}>{rec?.items?.[it.id] ? rec.items[it.id].pending : "—"}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMoney(it.rate)}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{fmtMoney(it.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <div className="ml-auto w-64 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Subtotal</span><span className="font-mono">{po.subtotal}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">GST</span><span className="font-mono">{po.tax_amount}</span></div>
            <div className="flex justify-between border-t border-border pt-1 font-semibold"><span>Total</span><span className="font-mono">{po.total_amount}</span></div>
          </div>
          {rec && (rec.grns || []).length > 0 && (
            <div className="rounded-md border border-border p-2.5 text-xs" data-testid="po-grn-list">
              <div className="mb-1 font-medium text-foreground">Goods received against this PO</div>
              {rec.grns.map((g) => <div key={g.id} className="flex justify-between py-0.5"><span className="font-mono">{g.number} · {g.date}</span><span>{Number(g.quantity).toLocaleString("en-IN")} · {g.status === "POSTED" ? "Posted" : "Draft (not counted)"}</span></div>)}
            </div>
          )}
          {rec && ["FULL", "PARTIAL", "NOT_RECEIVED"].includes(rec.status) && rec.grns.length === 0 && <p className="text-xs text-amber-600 dark:text-amber-400">No goods received against this PO yet — create a GRN when the material arrives.</p>}
          {po.remarks && <p className="text-xs text-muted-foreground">Remarks: {po.remarks}</p>}
          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
            <Button onClick={() => openPdf(`/print/purchase-orders/${po.id}`)} data-testid="po-view-print-button"><Printer className="h-4 w-4" /> Print / PDF</Button>
          </DialogFooter>
        </>)}
      </DialogContent>
    </Dialog>
  );
}

function CreatePODialog({ companies, vendors, rfqs, requisitions, projects, onCreated }) {
  const EMPTY_FORM = { company_id: "", project_id: "", vendor_id: "", po_date: "", rfq_id: "", requisition_id: "", tax_code_id: "" };
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [source, setSource] = useState("");
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [taxCodes, setTaxCodes] = useState([]);
  useResetOnClose(Boolean(open), () => { setForm(EMPTY_FORM); setSource(""); });      // closed without saving: opens fresh next time
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.rate,
  } : it)));

  useEffect(() => {
    if (open) api.get("/masters/tax-codes").then(({ data }) => setTaxCodes((data || []).filter((t) => t.is_active !== false))).catch(() => {});
  }, [open]);

  // Documents a PO can be copied from: open RFQs, and approved requisitions not yet converted
  const openRfqs = (rfqs || []).filter((r) => !["CLOSED", "CONVERTED", "CANCELLED"].includes(r.status));
  const openRequisitions = (requisitions || []).filter((r) => r.status === "APPROVED");
  const vendorName = (id) => (vendors || []).find((v) => v.id === id)?.name;

  const rateFromQuote = (rfq, invite, item) => {
    if (invite?.line_rates && invite.line_rates[item.id] != null) return String(invite.line_rates[item.id]);
    if (invite?.quoted_amount != null && rfq.items.length === 1) return (Number(invite.quoted_amount) / Number(item.quantity)).toFixed(2);
    return item.estimated_rate != null ? String(item.estimated_rate) : "";
  };

  const pickSource = (value) => {
    setSource(value);
    if (!value) return;
    const [kind, id] = value.split(":");
    if (kind === "rfq") {
      const rfq = openRfqs.find((r) => r.id === id);
      const invite = rfq.invited_vendors.find((v) => v.is_selected) || null;
      setForm({ ...form, company_id: rfq.company_id, project_id: rfq.project_id || form.project_id, vendor_id: invite?.vendor_id || form.vendor_id, rfq_id: rfq.id, requisition_id: "" });
      setItems(rfq.items.map((it) => ({ description: it.description, uom_id: it.uom_id || null, quantity: String(Number(it.quantity)), rate: rateFromQuote(rfq, invite, it) })));
      if (!invite) toast.info("No vendor is selected on this RFQ yet — choose the vendor below");
    } else {
      const pr = openRequisitions.find((r) => r.id === id);
      setForm({ ...form, company_id: pr.company_id, project_id: pr.project_id || form.project_id, rfq_id: "", requisition_id: pr.id });
      setItems(pr.items.map((it) => ({ description: it.description, uom_id: it.uom_id || null, quantity: String(Number(it.quantity)), rate: it.estimated_rate != null ? String(it.estimated_rate) : "" })));
    }
  };

  const reset = () => { setForm(EMPTY_FORM); setSource(""); setItems([{ ...EMPTY_ITEM }]); };
  const total = items.reduce((sum, it) => sum + Number(it.quantity || 0) * Number(it.rate || 0), 0);
  const inr = (n) => `₹${n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/procurement/purchase-orders", { ...form, project_id: form.project_id || null, tax_code_id: form.tax_code_id || null, rfq_id: form.rfq_id || null, requisition_id: form.requisition_id || null, items: items.map((it) => ({ ...it, wbs_item_id: it.wbs_item_id || null })) });
      toast.success("Purchase order created");
      reset();
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild><Button data-testid="create-po-button"><Plus className="h-4 w-4" /> New PO</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader><DialogTitle>New Purchase Order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-po-form">
          <div className="rounded-lg border border-dashed border-border p-3">
            <Label>Copy from <span className="font-normal text-muted-foreground">(fills company, vendor, items and rates)</span></Label>
            <Select value={source} onValueChange={pickSource}>
              <SelectTrigger data-testid="po-source-select" className="mt-1.5"><SelectValue placeholder="Start blank, or pick an RFQ / requisition" /></SelectTrigger>
              <SelectContent>
                {openRfqs.map((r) => {
                  const sel = r.invited_vendors.find((v) => v.is_selected);
                  return (
                    <SelectItem key={r.id} value={`rfq:${r.id}`}>
                      RFQ {r.number}{sel ? ` · ${vendorName(sel.vendor_id) || "selected vendor"}${sel.quoted_amount != null ? ` · ₹${Number(sel.quoted_amount).toLocaleString("en-IN")}` : ""}` : " · no vendor selected"}
                    </SelectItem>
                  );
                })}
                {openRequisitions.map((r) => (
                  <SelectItem key={r.id} value={`pr:${r.id}`}>Requisition {r.number} · {r.items.length} item(s) · estimated rates</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {openRfqs.length === 0 && openRequisitions.length === 0 && (
              <p className="mt-1.5 text-[11px] text-muted-foreground">No open RFQs or approved requisitions to copy from.</p>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <CompanyField label="Company" value={form.company_id} onChange={(v) => setForm({ ...form, company_id: v })} testId="po-company-select" />
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="po-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{partyLabel(v)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="po-date-input" value={form.po_date} onChange={set("po_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(the site this material is for — its cost is booked there)</span></Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="po-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {!form.project_id && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Without a project, this material's cost won't appear in any project's finance.</p>}
          </div>
          <div>
            <Label>Items</Label>
            <div className="mt-1.5 space-y-2">
              <div className="flex items-center gap-2 px-0.5 text-[11px] text-muted-foreground">
                <span className="flex-1">Description</span><span className="w-24">Qty</span><span className="w-24">Rate</span><span className="w-28 text-right">Amount</span><span className="w-14 text-center" title="Tick for machines / vehicles / equipment the company keeps (fixed assets) — not site material">Asset?</span>{items.length > 1 && <span className="w-9" />}
              </div>
              <p className="px-0.5 text-[11px] text-muted-foreground">Link each line to a WBS activity so its cost shows phase-wise in Cost to Completion.</p>
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`po-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} data-testid={`po-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`po-item-qty-input-${idx}`} />
                    <Input required type="number" step="0.01" placeholder="Rate" className="w-24" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} data-testid={`po-item-rate-input-${idx}`} />
                    <span className="w-28 text-right font-mono text-xs text-foreground">{inr(Number(it.quantity || 0) * Number(it.rate || 0))}</span>
                    <span className="flex w-14 justify-center"><input type="checkbox" title="Fixed asset (machine / vehicle / equipment)" checked={Boolean(it.is_capital)} onChange={(e) => updateItem(idx, "is_capital", e.target.checked)} data-testid={`po-item-capital-${idx}`} /></span>
                    {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))} data-testid={`remove-po-item-button-${idx}`}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`po-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-po-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label>GST / tax code</Label>
              <Select value={form.tax_code_id || "none"} onValueChange={(v) => setForm({ ...form, tax_code_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="po-tax-select" className="mt-1.5"><SelectValue placeholder="No tax" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No tax</SelectItem>
                  {taxCodes.map((t) => <SelectItem key={t.id} value={t.id}>{t.code} — {t.name} ({Number(t.rate)}%)</SelectItem>)}
                </SelectContent>
              </Select>
              {taxCodes.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">Add tax codes in Masters → Tax Codes.</p>}
            </div>
            <div className="space-y-0.5 self-end text-right text-sm" data-testid="po-total">
              {(() => {
                const rate = Number(taxCodes.find((t) => t.id === form.tax_code_id)?.rate || 0);
                const tax = (total * rate) / 100;
                return (
                  <>
                    <div className="text-muted-foreground">Subtotal {inr(total)}</div>
                    <div className="text-muted-foreground">GST {rate}% {inr(tax)}</div>
                    <div className="font-semibold text-foreground">Total {inr(total + tax)}</div>
                  </>
                );
              })()}
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-po-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
